/**
 * Deployment site configuration for the Mall SPA.
 *
 * Reads `window.__PPOS_SITE_CONFIG__` published by /site-config.js, which is
 * loaded by index.html before the app bundle. That file is per-deployment data,
 * NOT part of the compiled app — so the same build can serve several domains
 * with different names without a rebuild.
 *
 * Resolution order for the product name:
 *   1. `domains[<matched root domain>].brand`  — per-domain rename
 *   2. built-in house brand for a known house domain (ppos.my → PPOS)
 *   3. top-level `brand`                        — whole-deployment rename
 *   4. 'PharmaPOS'
 */

/**
 * Which product UI a deployment runs.
 *
 *   pharmacy — pharmacy storefront (default).
 *   retail   — simplified stall/shop wording and icons.
 *
 * Decided per hostname from /site-config.js, never compiled in.
 */
export type SiteProfile = 'pharmacy' | 'retail';

export interface DomainSiteConfig {
  /** Product name for hosts of this domain. Wins over the top-level `brand`. */
  brand?: string;
  /** Product UI for hosts of this domain (defaults to 'pharmacy'). */
  profile?: SiteProfile;
}

export interface SiteConfig {
  /** Fallback product name (white-label servers rename it). */
  brand?: string;
  /** Fallback UI profile for hosts that match no `domains` key. */
  profile?: SiteProfile;
  /** Keyed by ROOT domain; the most specific matching suffix wins. */
  domains?: Record<string, DomainSiteConfig>;
}

declare global {
  interface Window {
    __PPOS_SITE_CONFIG__?: SiteConfig;
  }
}

const HOUSE_BRAND = 'PharmaPOS';

/**
 * Built-in brand per house domain. Keeps the name correct even when the
 * deployed /site-config.js is missing or predates per-domain branding.
 */
const HOUSE_DOMAIN_BRANDS: Record<string, string> = {
  'ppos.my': 'PPOS',
  'pharmapos.com': 'PharmaPOS',
};

function config(): SiteConfig {
  return (typeof window !== 'undefined' && window.__PPOS_SITE_CONFIG__) || {};
}

/**
 * Longest key in `keys` that `hostname` equals or is a subdomain of.
 * Matches on a dot boundary so `notppos.my` never matches `ppos.my`.
 */
function matchSuffix(hostname: string, keys: string[]): string | null {
  const sorted = [...keys].sort((a, b) => b.length - a.length);
  return sorted.find((key) => hostname === key || hostname.endsWith(`.${key}`)) ?? null;
}

/** Root-domain key from site-config that matches `hostname`, or null. */
export function matchedDomainKey(hostname: string = location.hostname): string | null {
  return matchSuffix(hostname, Object.keys(config().domains ?? {}));
}

/** Product name for the tab title and the storefront header/logo. */
export function siteBrand(hostname: string = location.hostname): string {
  const key = matchedDomainKey(hostname);
  const domainBrand = key ? config().domains?.[key]?.brand?.trim() : undefined;
  if (domainBrand) return domainBrand;

  const houseKey = matchSuffix(hostname, Object.keys(HOUSE_DOMAIN_BRANDS));
  const houseBrand = houseKey ? HOUSE_DOMAIN_BRANDS[houseKey] : undefined;
  if (houseBrand) return houseBrand;

  const brand = config().brand?.trim();
  return brand && brand.length > 0 ? brand : HOUSE_BRAND;
}

/**
 * UI profile for the current host.
 *
 * Resolution order:
 *   1. `domains[<matched root domain>].profile` — per-domain (ppos.my → retail)
 *   2. top-level `profile`                      — whole-deployment
 *   3. 'pharmacy'
 *
 * mall.ppos.my and mall.pharmapos.com are served from the SAME web root, so
 * the decision must come from the deployment's site-config.js.
 */
export function siteProfile(hostname: string = location.hostname): SiteProfile {
  const key = matchedDomainKey(hostname);
  const domainProfile = key ? config().domains?.[key]?.profile : undefined;
  if (domainProfile) return domainProfile;
  return config().profile ?? 'pharmacy';
}

/** True when this deployment shows the simplified retail-stall wording/icons. */
export function isRetailProfile(hostname: string = location.hostname): boolean {
  return siteProfile(hostname) === 'retail';
}
